import React from 'react';
export const injectImports = ({ sduiImports, config }) => {

  const dynamicLoad = {};
  const keys = Object.keys(sduiImports);
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (/Slot[A-Z]\d+Index\d+/.test(key)) {
      const [, s, i, n] = /Slot([A-Z])(\d+)Index(\d+)/.exec(key);
      const SectionIndex = s + i;
      const nAsInt = parseInt(n, 10);
      if (!dynamicLoad[SectionIndex]) {
        dynamicLoad[SectionIndex] = {};
      }
      dynamicLoad[SectionIndex][nAsInt] = sduiImports[key];
    }
  }
  let slotIndex = 0;
  let section = '';

  const slotProps = config.slots.reduce((acc, cur, index) => {

    if (cur.disabled) {
      return acc;
    }

    if (section !== cur.section) {
      slotIndex = 0;
      section = cur.section;
    }
    // eslint-disable-next-line no-plusplus
    slotIndex++;

    return {
      ...acc,
      [`slot${cur.section}${slotIndex}`]: {
        ...cur,
        slot: slotIndex,
        components: cur.isSDUI
          ? cur.components.map((com, n) => {
            if (Array.isArray(com)) {
              return com.map((cp, nd) => ({
                ...cp, load: dynamicLoad[cur.section + slotIndex][n]
              }));
            }
            return { ...com, load: dynamicLoad[cur.section + slotIndex][n] };
          })
          : cur.components,
      },
    };
  }, {});

  return {
    ...config,
    slots: slotProps,
  };
};

export const parseGrid = (gridItem) => {
  const { ...breakpoints } = gridItem;
  if (!breakpoints) return {};

  const keys = Object.keys(breakpoints);
  const classNames = keys
    .map((breakpoint) => {
      if (!breakpoints[breakpoint]) return null;
      if (breakpoint === '__typename') return null;
      const { cols, sticky, classNames: cnames = [] } = breakpoints[breakpoint];
      return (cnames || []).map((name) => {
        const suiClass = name.indexOf('sui-') === -1 ? `sui-${name}` : name;
        return breakpoint === 'default' ? suiClass : `${breakpoint}:${suiClass}`;
      }).join(' ');
    })
    .filter((val) => val);

  return classNames.join(' ');
};

export const hasSDUI = (config) => {
  return config.slots.some((slot) => {
    if (slot.isSDUI) return true;

    if (slot.isAccordion) {

      const components = slot.components;
      return components.some((headOrBody) => {
        return headOrBody.some((component) => {
          return component.isSDUI;
        });
      });
    }
    return false;
  });
};

export const getSduiComponents = (config) => {
  return config.slots.filter((slot) => {
    if (slot.isSDUI) return true;

    // @todo accordion
    // if (slot.isAccordion) {

    //   const components = slot.components as Slot[][];
    //   return components.some((headOrBody) => {
    //     return headOrBody.some((component) => {
    //       return component.isSDUI;
    //     });
    //   });
    // }
    return false;
  });
};

// when sdui data comes back from the api it has to be injected into the config prop data
// its possible sdui can change the prop values.
export const injectSduiDataToSlot = (slot, data, slotName) => {
  try {
    if (data?.slots?.[slotName]) {
      if (data.slots[slotName].component) {
        const sduiComponent = data.slots[slotName].component.data;
        const index = data.slots[slotName].component.index;
        const component = slot.components[index];
        const currentProps = component.props;

        component.props = {
          ...currentProps,
          ...sduiComponent.props,
        };
      } else if (data.slots[slotName].accordion) {
        const sduiAccordionData = data.slots[slotName].accordion;
        slot.components.forEach((headOrBody, headOrBodyIndex) => {
          (headOrBody).forEach((innerSlot, accordionIndex) => {
            (innerSlot.components).forEach((component, componentIndex) => {
              const sduiData = sduiAccordionData[headOrBodyIndex][accordionIndex];
              if (componentIndex === sduiData.index) {
                // hydrator may not be extended
                if (sduiData.data.props.hydrator) {
                  delete sduiData.data.props.hydrator;
                }
                // eslint-disable-next-line
                component.props = {
                  ...component.props,
                  ...(sduiData.data.props || {})
                };
              }
            });
          });
        });
      }
    }
  } catch (ex) {
    console.log(ex);
  }
};

// A decision can target one entry of a re-used slot id with "<slotId>__<entryId>" and
// otherwise applies to every CMS slot sharing the "<slotId>".
export const getContentDecision = (contentDecisions, slotName, entryId) => (
  (entryId !== undefined && entryId !== null ? contentDecisions?.[`${slotName}__${entryId}`] : undefined)
  ?? contentDecisions?.[slotName]
);

export const getMediaCollectionIndex = (contentDecisions, slotName, mediaCollectionArray, entryId) => {
  const decision = getContentDecision(contentDecisions, slotName, entryId);
  const index = Number(String(decision).split(':')[1]);
  const isInBounds = index >= 0 && index < (mediaCollectionArray?.length || 0);
  return isInBounds ? index : 0;
};

// Content decision contract: "<OptionalSlotOverride>:<mediaCollectionIndex>:[<variants>]",
// where variants is "<mediaIndex>:<variantIndex>" pairs, e.g. [0:1,1:5] (resolved by the API).
// Returns the optional override slot id (first segment), or undefined when it is
// missing, blank, or just a placeholder identical to the target slot.
export const getSlotOverrideId = (contentDecisions, slotName, entryId) => {
  const decision = getContentDecision(contentDecisions, slotName, entryId);
  if (typeof decision !== 'string') return undefined;
  const overrideId = decision.split(':')[0].trim();
  return overrideId && overrideId !== slotName ? overrideId : undefined;
};

const MOBILE_CHANNELS =['mobile', 'mobileApp'];

const getPreloadImageUri = (media, isMobile) => {
  const sources = isMobile ? media?.sourceMobile : media?.sourceDesktop;
  return sources?.[0]?.uri;
};

const buildSlotSlug = (slot, slotIndex) => `${slot.id}__${slot.entryId || slotIndex}`;

// Tolerates counts that are strings ('2'), floats (2.9), too large for the
// available media, zero/negative, or missing/non-numeric entirely.
const getPreloadCount = (rawCount, maxLength) => {
  const parsed = Number(rawCount);
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  return Math.min(Math.floor(parsed), maxLength);
};

export const getPreloadImages = ({ slotMap, cmsSlots, channel, contentDecisions }) => {
  const isMobile = MOBILE_CHANNELS.includes(channel);
  const preloadImages = [];
  const seenUrls = new Set();

  const cmsSlotsBySlug = new Map(
    cmsSlots.map((slot, slotIndex) => [buildSlotSlug(slot, slotIndex), slot])
  );

  Object.values(slotMap).forEach((slotConfig) => {
    (slotConfig?.components || []).forEach((component) => {
      const marker = component?.props?.$preloadImages;
      if (!marker) return;

      const cmsSlot = cmsSlotsBySlug.get(slotConfig.cmsSlug || slotConfig.slug);
      if (!cmsSlot) return;

      // Only the mediaCollection the experiment actually selected is rendered,
      // so that's the only one whose images are worth preloading.
      const mediaCollectionIndex = getMediaCollectionIndex(
        contentDecisions, cmsSlot.id, cmsSlot.mediaCollections, cmsSlot.entryId
      );
      const media = cmsSlot.mediaCollections?.[mediaCollectionIndex]?.media || [];

      const count = getPreloadCount(isMobile ? marker.$mobile : marker.$desktop, media.length);
      const exportName = component.export || 'Component';

      for (let i = 0; i < count; i += 1) {
        const uri = getPreloadImageUri(media[i], isMobile);
        // Skip images already captured by another component to avoid
        // preloading the same asset twice.
        if (uri && !seenUrls.has(uri)) {
          seenUrls.add(uri);
          preloadImages.push({ preloadedImageUrl: uri, preloadedImageId: `preload${exportName}Image${i}` });
        }
      }
    });
  });

  return preloadImages;
};

export const mergeProductClientOnlyProduct = {
  productClientOnlyProduct: {
    queries: ['product', 'clientOnlyProduct'],
    fn: ({ query, mounted, allQueryKeys }) => {

      if (!mounted) return query;

      if (allQueryKeys.includes('product') && allQueryKeys.includes('clientOnlyProduct')) {
        if (query === 'product' || query === 'clientOnlyProduct') {
          return 'productClientOnlyProduct';
        }
      }

      return query;
    }
  }
};
